import { Loader2, Users, Wallet } from "lucide-react";

import type { ClaimFinancialHealth } from "@upclaim/sdk/src/data-contracts";

import { formatEur } from "@/lib/currency";

import { useClaimFinancialHealth, useUpdatePaymentCounterparty } from "../api/use-traveler-payments";
import { type CounterpartyStatus, CounterpartyStatusSelect } from "./counterparty-status-select";

type FinancialFamily = ClaimFinancialHealth["passengers"]["families"][number];

/** Mirrors FamilyPaymentBreakdown.getFamilyDisplayName (Vue). */
export function getFamilyDisplayName(family: FinancialFamily): string {
  const firstPassengerFullName = (family as { firstPassengerFullName?: string | null })
    .firstPassengerFullName;
  if (family.passengerCount === 1 && firstPassengerFullName) {
    return firstPassengerFullName;
  }
  return `Famille ${family.familyName || "—"}`;
}

interface FamilyPaymentBreakdownProps {
  claimId: string;
  claimNumber: number;
}

/** Port of financial/FamilyPaymentBreakdown.vue — per-family net breakdown + counterparty status. */
export function FamilyPaymentBreakdown({ claimId, claimNumber }: FamilyPaymentBreakdownProps) {
  const { data, isPending } = useClaimFinancialHealth(claimNumber);
  const updateCounterparty = useUpdatePaymentCounterparty(claimId);

  const families = data?.passengers.families ?? [];

  function updateStatus(counterpartyId: string, nextStatus: CounterpartyStatus) {
    if (!counterpartyId) {
      return;
    }
    updateCounterparty.mutate({ counterpartyId, status: nextStatus });
  }

  return (
    <div className="rounded-[10px] border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50">
          <Wallet className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="text-left">
          <h3 className="text-sm font-semibold text-gray-900">Paiements aux voyageurs</h3>
          <p className="text-xs text-gray-500">Net recalculé à partir des montants reçus</p>
        </div>
      </div>

      {isPending && !data ? (
        <div className="py-4 text-center text-sm text-gray-500">
          <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
          Chargement…
        </div>
      ) : !data || !families.length ? (
        <div className="rounded-[10px] border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs text-gray-500">
          Aucune donnée de paiement disponible.
        </div>
      ) : (
        <div className="space-y-3">
          {families.map((family) => (
            <div
              key={family.familyClaimAcquisitionId}
              className="overflow-hidden rounded-[10px] border border-gray-200 bg-white"
            >
              {/* Card Header */}
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
                  <Users className="h-4 w-4 text-slate-500" />
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <h4 className="text-sm font-semibold text-gray-900">{getFamilyDisplayName(family)}</h4>
                  {family.passengerCount > 1 ? (
                    <p className="text-xs text-gray-500">{family.passengerCount} passagers</p>
                  ) : null}
                </div>
                {family.counterpartyId ? (
                  <CounterpartyStatusSelect
                    value={family.status as CounterpartyStatus}
                    disabled={updateCounterparty.isPending}
                    onChange={(status) => updateStatus(family.counterpartyId, status)}
                  />
                ) : null}
                <span className="text-base font-semibold whitespace-nowrap text-emerald-600 tabular-nums">
                  {formatEur(family.netCents)}
                </span>
              </div>

              {/* Card Body */}
              <div className="space-y-1.5 border-t border-gray-100 px-4 py-3">
                {family.demands.map((demand, index) => (
                  <div key={`${demand.monetaryDemandId}-${index}`} className="flex justify-between text-sm">
                    <span className="text-gray-700">{demand.expenseName || demand.displayText}</span>
                    <span className="text-gray-900 tabular-nums">{formatEur(demand.amountCents)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm text-gray-400">
                  <span>− Commissions</span>
                  <span className="tabular-nums">
                    −{formatEur(family.upclaimCommissionCents + family.travelAgencyCommissionCents)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
